import { Fragment } from 'react';
import { LinearGradient } from '@visx/gradient';
import { DatumObject } from '@visx/shape/lib/types';
import { AnimatedAreaSeries, LineSeries } from '@visx/xychart';

import { thresholdTheme } from './common/themes';
import { useChartProps } from './common/use-chart-props';
import { BaseChart } from './internal/base-chart';
import { ChartTooltip } from './internal/chart-tooltip';
import { ChartWithDataProps } from './charts.model';

export interface ThresholdChartProps<T extends {}> extends ChartWithDataProps<T> {
    threshold: 'change' | 'start';
}

export const ThresholdChart = <T extends DatumObject>({
    id,
    data,
    dateKey,
    keys,
    threshold = 'change',
    theme = thresholdTheme,
    ...rest
}: ThresholdChartProps<T>) => {
    const chartProps = useChartProps({
        dateKey,
        data,
        keys,
        theme,
        threshold,
    });
    const { offset, baseline } = chartProps.bounds;
    const defIds = {
        positive: `${id}-positive-gradient`,
        negative: `${id}-negative-gradient`,
        line: `${id}-line-gradient`,
    };

    return (
        <BaseChart
            margin={{ top: 0, left: 0, bottom: 0, right: 0 }}
            legend={false}
            xAxis={false}
            yAxis={false}
            {...chartProps}
            {...rest}
        >
            {keys.map(key => {
                const props = {
                    ...chartProps.bindAccessors(key),
                    dataKey: String(key),
                    lineProps: {
                        stroke: `url(#${defIds.line})`,
                        strokeWidth: 1,
                    },
                };
                return (
                    <Fragment key={`${String(key)}-series`}>
                        <AnimatedAreaSeries {...props} fill={`url(#${defIds.positive})`} />
                        <AnimatedAreaSeries {...props} fill={`url(#${defIds.negative})`} />
                    </Fragment>
                );
            })}
            <LineSeries
                dataKey="dateKey"
                xAccessor={x => x[dateKey]}
                yAccessor={() => baseline}
                data={data}
                stroke="var(--color-surface-selected)"
                strokeWidth={1}
                strokeDasharray={4}
            />

            <LinearGradient
                id={defIds.positive}
                toOffset={offset}
                from="rgba(94, 204, 160, .5)"
                to="rgba(94, 204, 160, 0.00)"
            />
            <LinearGradient
                id={defIds.negative}
                fromOffset={offset}
                from="rgba(216, 96, 55, 0.00)"
                to="rgba(216, 96, 55, .5)"
            />
            <LinearGradient
                id={defIds.line}
                fromOffset={offset}
                toOffset={offset}
                from="rgba(94, 204, 160, 1)"
                to="rgba(216, 96, 55, 1)"
            />
            <ChartTooltip<T> glyphs dateKey={dateKey} {...rest} />
        </BaseChart>
    );
};
